import React from 'react';
import { Box, Typography, Card, styled, Container, Breadcrumbs, Link, Button } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import createTicketImg from '../assets/images/light/create ticket.png';
import trackTicketImg from '../assets/images/light/track-ticket.png';

const PageContainer = styled(Box)({
  width: '100%',
  minHeight: '100vh',
  padding: '32px',
  backgroundColor: '#EEF5F6',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  fontFamily: 'Inter, sans-serif',
});

const ContentContainer = styled(Container)({
  maxWidth: '1440px',
  margin: '0 auto',
  padding: '18px 16px 0',
  width: '100%',
});

const Header = styled(Box)({
  width: '100%',
  marginBottom: '16px',
  textAlign: 'left',
  maxWidth: '1440px',
  padding: '0 80px', // Align with card content
  margin: '0 auto 16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  '@media (max-width: 1024px)': {
    padding: '0 16px',
  },
});

const BreadcrumbContainer = styled(Breadcrumbs)({
  width: '193px',
  height: '21px',
  flexShrink: 0,
  '& .MuiBreadcrumbs-separator': {
    margin: '0 4px',
    color: '#4299D4', // Match Dashboard text color
    fontFamily: 'Proxima Nova',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal',
    '& > span': {
      color: '#4299D4 !important', // Force the separator color
    }
  },
  '& .MuiBreadcrumbs-li': {
    display: 'flex',
    alignItems: 'center',
  },
  marginTop: '-170px',  // Moved up by 10px (from -110px)
  marginRight: '0',    // Removed right margin to move left
  marginLeft: '-180px',  // Added left margin to move further left
  fontFamily: 'Proxima Nova',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  '@media (max-width: 1024px)': {
    marginRight: '0',
  },
});

const BreadcrumbLink = styled(Link)({
  cursor: 'pointer',
  textDecoration: 'none',
  fontFamily: 'Proxima Nova',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  color: '#6D6D6D',
  '&:hover': {
    textDecoration: 'none',
    color: '#1674B4',
  },
  '&.MuiTypography-root': {
    display: 'flex',
    alignItems: 'center',
  },
});

const Title = styled(Typography)({
  fontSize: '30px',
  fontWeight: 600,
  color: '#1674B4',
  margin: 0,
  padding: 0,
  lineHeight: '32px',
  fontFamily: 'Proxima Nova',
  fontStyle: 'normal',
  textAlign: 'left',
});

const Subtitle = styled(Typography)({
  fontSize: '14px',
  color: '#2E2E2E',
  fontWeight: 600,
  textAlign: 'left',
  lineHeight: '13px', 
  margin: 0,
  padding: 0,
  marginBottom: '4px', 
});

const HelperText = styled(Typography)({
  fontSize: '14px',
  color: '#2E2E2E',
  textAlign: 'left',
  lineHeight: '13px', 
  fontWeight: 400,
  margin: 0,
  padding: 0,
});

const CardsContainer = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '35px',
  width: '100%',
  maxWidth: '1440px',
  margin: '0 auto',
  padding: '0 16px',
  '& > *': {
    margin: '0 17.5px', // Half of the gap to distribute space evenly
    '@media (max-width: 1440px)': {
      margin: '0 8px',
    },
  },
  '@media (max-width: 1024px)': {
    '& > *': {
      maxWidth: '470px',
      width: '100%',
      margin: '0 auto',
    }
  },
});

const StyledCard = styled(Card)({
  width: '470px',
  height: '124px',
  borderRadius: '8px',
  boxShadow: '2px 2px 2px 0px rgba(0, 0, 0, 0.25)',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '24px',
  backgroundColor: '#FFFFFF',
  border: 'none',
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '3px 3px 3px 0px rgba(0, 0, 0, 0.3)',
  },
  '@media (max-width: 1024px)': {
    width: '100%',
    maxWidth: '470px',
  },
});

const CardImage = styled('img')({
  width: '80px',
  height: '51.846px',
  marginRight: '24px',
  objectFit: 'contain',
  flexShrink: 0,
});

const CardContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});

const CardTitle = styled(Typography)({
  fontSize: '20px',
  fontWeight: 600,
  margin: 0,
  color: '#1674B4',
  textAlign: 'left',
  lineHeight: '24px',
  padding: 0,
});

const CardDescription = styled(Typography)({
  color: '#6D6D6D',
  lineHeight: '20px',
  textAlign: 'left',
  fontSize: '14px',
  margin: 0,
  padding: 0,
  paddingTop: '2px',
});

function SupportDashboard() {
  const navigate = useNavigate();
  
  const handleDashboardClick = (event) => {
    event.preventDefault();
    navigate('/');
  };

  const menuItems = [
    {
      image: createTicketImg,
      title: 'Create Ticket',
      description: 'Submit a detailed support request to report issues, ask questions, or request help from the DevOps team.',
      className: 'create',
      onClick: () => navigate('/create-ticket')
    },
    {
      image: trackTicketImg,
      title: 'Track Tickets',
      description: 'Monitor the status, updates, and resolution progress of your submitted support tickets — all in one place.',
      className: 'track',
      onClick: () => navigate('/track-tickets')
    }
  ];

  return (
    <PageContainer>
      <ContentContainer>
        <Header>
          <Box>
            <Button
              component={RouterLink}
              to="/"
              sx={{
                color: '#4299D4',
                fontFamily: 'Proxima Nova',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                textTransform: 'none',
                textAlign: 'right',
                p: 0,
                mt: '-80px',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'none'
                }
              }}
            >
              &lt; Back
            </Button>
            <Title>Support Management</Title>
            <Box 
              sx={{ 
                width: '982px', 
                height: '1px',
                backgroundColor: '#B1B1B1',
                my: 2
              }} 
            />
            <Subtitle>Easily manage all your support interactions from one place.</Subtitle>
            <HelperText>Create new requests, track ticket status, and stay updated in real time.</HelperText>
          </Box>
          <BreadcrumbContainer aria-label="breadcrumb" separator=">">
            <BreadcrumbLink 
              onClick={handleDashboardClick}
              sx={{ 
                color: '#4299D4',
                '&:hover': {
                  textDecoration: 'none',
                  color: '#4299D4',
                }
              }}
            >
              Dashboard
            </BreadcrumbLink>
            <Typography 
              sx={{ 
                color: '#6D6D6D',
                fontFamily: 'Proxima Nova',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal'
              }}
            >
              Support Management
            </Typography>
          </BreadcrumbContainer>
        </Header>
        
        <CardsContainer>
          {menuItems.map((item, index) => (
            <StyledCard key={index} elevation={0} onClick={item.onClick}>
              <CardImage 
                src={item.image} 
                alt={item.title}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://via.placeholder.com/64?text=${item.title.split(' ')[0]}`;
                }}
              />
              <CardContent>
                <CardTitle variant="h3">{item.title}</CardTitle>
                <CardDescription variant="body1">
                  {item.description}
                </CardDescription>
              </CardContent>
            </StyledCard>
          ))}
        </CardsContainer>
      </ContentContainer>
      {/* Chatbot and Summary components are now in DashboardLayout */}
    </PageContainer>
  );
}

export default SupportDashboard;
